/**
 * @module game/ScoreManager
 * Tracks score, lives, combo chain, and score multiplier per CONFIG values.
 *
 * Multiplier thresholds come from CONFIG.combo.multiplierThresholds so
 * designers can tune them without touching code.
 *
 * Combo rules:
 *  - slashFruit() increments combo and applies multiplier + doublePoints.
 *  - missedFruit() in Classic mode resets combo and costs 1 life.
 *  - Critical fruits earn CONFIG.fruit.criticalMultiplier on top of combo
 *    multiplier (multiplicative, not additive).
 */

import { CONFIG } from '../game-config.js';
import type { GameMode } from '../game-config.js';
import type { ScoreState } from '../types.js';

/** Manages all scoring state for one game session. */
export class ScoreManager {
  private _score = 0;
  private _lives: number;
  private _combo = 0;
  private _missCount = 0;
  private readonly _mode: GameMode;
  /**
   * Classic: true when lives reach 0 or bomb is hit.
   * Zen: always false — Zen ends by timer, managed externally.
   */
  private _classicGameOver = false;

  constructor(initialLives: number, mode: GameMode) {
    this._lives = initialLives;
    this._mode = mode;
  }

  /**
   * Awards points for a slashed fruit and increments the combo chain.
   *
   * @param fruitType - Fruit type key from CONFIG.fruits (for per-type points).
   * @param isCritical - Whether this is a critical (golden) fruit.
   * @param doublePoints - Whether the Double Points power-up is active.
   */
  slashFruit(
    fruitType: keyof typeof CONFIG.fruits,
    isCritical: boolean,
    doublePoints: boolean,
  ): void {
    this._combo += 1;

    const fruitConfig = CONFIG.fruits[fruitType];
    const base = fruitConfig.basePoints;
    const critMul = isCritical ? CONFIG.fruit.criticalMultiplier : 1;
    const comboMul = this.multiplier;
    const doubleMul = doublePoints ? 2 : 1;

    this._score += base * critMul * comboMul * doubleMul;
  }

  /**
   * Handles a fruit escaping off-screen without being slashed.
   * In Classic mode: resets combo and deducts one life.
   * In Zen mode: no life penalty (no lives system), but combo resets.
   */
  missedFruit(): void {
    this._combo = 0;
    this._missCount += 1;
    if (this._mode === 'classic') {
      this._lives = Math.max(0, this._lives - 1);
      if (this._lives === 0) this._classicGameOver = true;
    }
  }

  /**
   * Applies the bomb penalty: immediate game over in Classic mode.
   * In Zen mode this is a no-op (no bombs in Zen).
   */
  hitBomb(): void {
    this._combo = 0;
    if (this._mode === 'classic') {
      this._lives = 0;
      this._classicGameOver = true;
    }
  }

  /**
   * Whether the session has ended.
   * Classic: true when lives reach 0 (from missed fruit or bomb).
   * Zen: always false — the game loop controls session end via the countdown timer.
   */
  get isGameOver(): boolean {
    return this._classicGameOver;
  }

  /**
   * Current score multiplier from CONFIG.combo.multiplierThresholds.
   * Looks up the highest threshold key that the current combo meets.
   */
  get multiplier(): number {
    const thresholds = CONFIG.combo.multiplierThresholds;
    const keys = Object.keys(thresholds)
      .map(Number)
      .sort((a, b) => b - a); // descending

    for (const k of keys) {
      if (this._combo >= k) return thresholds[k] ?? 1;
    }
    return 1;
  }

  /** Read-only snapshot of current state for the UI / HUD. */
  get state(): ScoreState {
    return {
      score: this._score,
      lives: this._lives,
      combo: this._combo,
      multiplier: this.multiplier,
      mode: this._mode,
      missCount: this._missCount,
    };
  }

  /** Resets all counters for a new game session. */
  reset(initialLives: number): void {
    this._score = 0;
    this._lives = initialLives;
    this._combo = 0;
    this._missCount = 0;
    this._classicGameOver = false;
  }
}
